/**
 * Pending public action storage — resume write actions after sign-in.
 *
 * When an anonymous visitor on a public share link clicks a write action
 * (Approve, Save), we capture their intent before redirecting to sign-in.
 * This module stores that intent in sessionStorage and retrieves it after
 * the sign-in flow completes, so we can ask the user to confirm before
 * actually performing the action.
 *
 * Why sessionStorage: The intent is scoped to a single tab and a single
 * visitor session — it expires after sign-in (or 15 minutes, whichever
 * comes first). sessionStorage matches that lifetime exactly.
 */

const KEY = 'discovr:pendingPublicAction';
export const PENDING_ACTION_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

type PendingPublicActionBase = {
  campCode: string;
  tail: string;
  entryUsername: string;
  createdAt: number;
};

/**
 * Base shape for navigation-intent actions (clicking a nav item or a status
 * pill before signing in). Unlike the three write-action variants above,
 * these aren't scoped to one creator — there is no `entryUsername` to
 * capture — so they get their own, narrower base shape instead of forcing an
 * empty string through `PendingPublicActionBase`.
 */
type PendingPublicActionNavBase = {
  campCode: string;
  tail: string;
  createdAt: number;
};

export type PendingPublicAction =
  | (PendingPublicActionBase & { type: 'approve_script'; version: number })
  | (PendingPublicActionBase & { type: 'approve_content'; version: number })
  | (PendingPublicActionBase & {
      type: 'save_deliverables';
      selectedMainIds: string[];
      selectedAddonIds: string[];
    })
  | (PendingPublicActionNavBase & { type: 'navigate'; tab: string })
  | (PendingPublicActionNavBase & { type: 'select_pill'; tab: string; pill: string })
  | (PendingPublicActionBase & { type: 'open_insights'; tab: string; insightsTab?: string })
  | (PendingPublicActionBase & { type: 'approve_creator'; tab: string })
  | (PendingPublicActionBase & { type: 'reject'; tab: string });

const isBrowser = (): boolean => typeof window !== 'undefined';

type ShapeValidationResult =
  | { valid: true; action: PendingPublicAction }
  | { valid: false };

/**
 * Parses raw sessionStorage JSON and validates it has the shape of a
 * `PendingPublicAction` (required fields present, correct types, `type` is
 * one of the known literals, string fields non-empty). Does NOT check
 * expiry and does NOT touch storage — pure parse+validate, shared by
 * `readPendingPublicAction` (which additionally checks expiry and clears
 * storage on failure) and `peekPendingPublicActionExpired` (which must
 * never mutate storage).
 */
/** Write-action types that are scoped to one creator — require a non-empty
 *  `entryUsername`. Nav-intent types (`navigate` / `select_pill`) don't.
 *  `open_insights` is scoped to a creator (like the write actions) AND
 *  carries a `tab` (like the nav-intent types) — see its checks below. */
const ENTRY_SCOPED_TYPES = [
  'approve_script',
  'approve_content',
  'save_deliverables',
  'open_insights',
  'approve_creator',
  'reject',
];
const NAV_TYPES = ['navigate', 'select_pill'];
const VALID_TYPES = [...ENTRY_SCOPED_TYPES, ...NAV_TYPES];

function parseStoredAction(stored: string): ShapeValidationResult {
  const action: unknown = JSON.parse(stored);

  // Validate shape: must have required fields with correct types
  if (
    typeof action !== 'object' ||
    action === null ||
    !('createdAt' in action) ||
    typeof (action as Record<string, unknown>).createdAt !== 'number' ||
    !('type' in action) ||
    !('campCode' in action) ||
    !('tail' in action)
  ) {
    return { valid: false };
  }

  const actionRecord = action as Record<string, unknown>;

  // Validate type is one of the valid literals
  if (typeof actionRecord.type !== 'string' || !VALID_TYPES.includes(actionRecord.type)) {
    return { valid: false };
  }

  // Validate string fields are non-empty strings
  if (
    typeof actionRecord.campCode !== 'string' ||
    actionRecord.campCode === '' ||
    typeof actionRecord.tail !== 'string' ||
    actionRecord.tail === ''
  ) {
    return { valid: false };
  }

  // Entry-scoped write actions (approve/save) additionally require a
  // non-empty `entryUsername`; nav-intent actions (navigate/select_pill)
  // don't carry one at all.
  if (ENTRY_SCOPED_TYPES.includes(actionRecord.type)) {
    if (typeof actionRecord.entryUsername !== 'string' || actionRecord.entryUsername === '') {
      return { valid: false };
    }
  }

  // Nav-intent actions must carry a non-empty `tab`; `select_pill` additionally
  // requires a non-empty `pill`. `open_insights`, `approve_creator`, and `reject`
  // also carry a `tab` (the roster tab to land back on) despite being entry-scoped
  // — checked here too rather than folding them into NAV_TYPES, since they don't
  // take a `pill`.
  if (NAV_TYPES.includes(actionRecord.type) || actionRecord.type === 'open_insights' || actionRecord.type === 'approve_creator' || actionRecord.type === 'reject') {
    if (typeof actionRecord.tab !== 'string' || actionRecord.tab === '') {
      return { valid: false };
    }
    if (
      actionRecord.type === 'select_pill' &&
      (typeof actionRecord.pill !== 'string' || actionRecord.pill === '')
    ) {
      return { valid: false };
    }
  }

  return { valid: true, action: action as PendingPublicAction };
}

/**
 * Store a pending public action in sessionStorage.
 *
 * The caller must set createdAt to Date.now() at the time of writing
 * (not inside this function — this codebase avoids Date.now() in pure
 * functions where the timestamp can be passed in).
 */
export function writePendingPublicAction(action: PendingPublicAction): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(action));
  } catch {
    // Storage disabled (Safari private mode, embedded webview). The action
    // just goes unrecorded — never break the UI over a storage error.
  }
}

/**
 * Retrieve a pending public action from sessionStorage.
 *
 * Returns null if no action is stored, if the stored data is malformed,
 * or if the action has expired (older than PENDING_ACTION_EXPIRY_MS).
 * Automatically clears expired or malformed data from storage.
 */
export function readPendingPublicAction(): PendingPublicAction | null {
  if (!isBrowser()) return null;
  try {
    const stored = window.sessionStorage.getItem(KEY);
    if (!stored) return null;

    const parsed = parseStoredAction(stored);
    if (!parsed.valid) {
      clearPendingPublicAction();
      return null;
    }

    // Check expiry
    if (Date.now() - parsed.action.createdAt > PENDING_ACTION_EXPIRY_MS) {
      clearPendingPublicAction();
      return null;
    }

    return parsed.action;
  } catch {
    // Malformed JSON or storage error
    clearPendingPublicAction();
    return null;
  }
}

/**
 * Peeks at raw storage (no mutation) to report whether a pending action WAS
 * present but has expired — distinct from "nothing was ever stored." Used
 * by the resume hook to show an "expired" toast instead of doing nothing,
 * since `readPendingPublicAction()` already silently discards expired
 * entries (clearing storage) with no way for a caller to tell "expired"
 * apart from "never set."
 *
 * Read-only: never calls `sessionStorage.removeItem`, and safe to call
 * before or independently of `readPendingPublicAction()` — it does not
 * itself count as a "read" for clearing purposes.
 *
 * Returns false if nothing is stored, if the stored data is malformed (we
 * can't tell if it was ever a real pending action), or if a valid action is
 * stored but not yet expired. Returns true only when a shape-valid action
 * is stored AND its `createdAt` is past `PENDING_ACTION_EXPIRY_MS`.
 */
export function peekPendingPublicActionExpired(): boolean {
  if (!isBrowser()) return false;
  try {
    const stored = window.sessionStorage.getItem(KEY);
    if (!stored) return false;

    const parsed = parseStoredAction(stored);
    if (!parsed.valid) return false;

    return Date.now() - parsed.action.createdAt > PENDING_ACTION_EXPIRY_MS;
  } catch {
    // Malformed JSON or storage error — can't tell if it was ever a real
    // pending action, so report "not expired" (same as "never set").
    return false;
  }
}

/**
 * Clear the pending public action from sessionStorage.
 *
 * Call this after the user has confirmed or rejected the pending action,
 * or when starting a fresh flow.
 */
export function clearPendingPublicAction(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
